/* Time on the client — the shop's timezone, bound once.
 *
 * The arithmetic and the formats are not here: they are `time.ts` in
 * @oneshot2pac/shared, which the server reads and writes days and instants
 * through as well. One module for both sides is the point — a day added up one
 * way in a service and another way in a screen is how a board and a list end up
 * disagreeing about what is late.
 *
 * What *is* here is the one thing only the client needs: somewhere to keep the
 * shop's timezone, so a screen can write `formatInstant(notice.postedAt)`
 * rather than threading the organisation's settings through every component
 * that draws a date.
 *
 * That makes this the one module in `domain/` holding a value that changes, and
 * it is deliberate — the same reasoning as `dragging` living outside the store.
 * It is configuration, not state: it is set once from the session on the way in
 * (and again when the office saves Settings → Organisation), nothing renders
 * from it directly, and a re-render never reads a different answer than the one
 * before it. Putting it in the store instead would mean every date on every
 * screen re-subscribing to a string that changes once a decade.
 *
 * The functions that take no timezone are the ones that do not need one: a day
 * is a square on a calendar and has no hour, so "Thu 3 Sep" reads the same in
 * Perth as in Brisbane. Only "what is today" and "when did this happen" need to
 * know where the shop is. */
import {
  DEFAULT_TIMEZONE,
  formatInstant as formatInstantIn,
  dayOfInstant as dayOfInstantIn,
  isTimezone,
  todayIn,
  type IsoDay,
} from '@oneshot2pac/shared';

/* Everything that needs no timezone passes straight through, so a screen has
   one import for dates rather than two. */
export {
  NOT_SET,
  addDays,
  compareDays,
  dayAsDate,
  daysBetween,
  formatDay,
  formatDayShort,
  formatWeekday,
  isDay,
  isWeekend,
  mondayOf,
  nextWorkday,
  weekdayOf,
  workdaysFrom,
  type IsoDay,
  type IsoInstant,
} from '@oneshot2pac/shared';

/** Where the shop is. Brisbane until the session says otherwise. */
let zone: string = DEFAULT_TIMEZONE;

/** Bind the shop's timezone. Called from the store when the session lands and
 *  when the office saves the organisation — nowhere else.
 *
 *  An unusable zone is ignored rather than taken: a typo saved in Settings
 *  should cost the shop nothing, and the server refuses one anyway. */
export function setShopTimezone(timezone: string | undefined): void {
  zone = timezone && isTimezone(timezone) ? timezone : DEFAULT_TIMEZONE;
}

/** Where the shop is, for the rare caller that wants to say so out loud. */
export function shopTimezone(): string {
  return zone;
}

/** What day it is where the shop is.
 *
 *  Every "is this late", every date a form opens on and every board comparison
 *  starts here. Not the tablet's idea of today: an iPad carried back from Perth
 *  is two hours behind the board, and a shop that reads two different todays on
 *  two screens has stopped trusting both. */
export function today(): IsoDay {
  return todayIn(zone);
}

/** An instant as the shop reads it — "3 Sep, 2:14 pm" in the shop's own
 *  timezone. The column is UTC; this is the only thing that turns it into a
 *  wall clock. */
export function formatInstant(
  value: string | null | undefined,
  opts: { year?: boolean } = {},
): string {
  return formatInstantIn(value, zone, opts);
}

/** The day an instant fell on where the shop is — what a notice board reads
 *  by, and how an instant is compared against a day. */
export function dayOfInstant(value: string | null | undefined): IsoDay | null {
  return dayOfInstantIn(value, zone);
}
